import { ReactElement, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  notification,
  rejectionError,
  transformCrudFormErrors,
  ValidationError,
} from '@ohpen/enso-ui';

import { BdmResponse, RegionResponse, bdmCreate, bdmUpdate } from '@pmp/api';
import { CrudFormProps } from '@pmp/types';

import { getApiErrorMessage } from '@pmp/utils';

import { getBdmSchema } from '../regionsBdmsFormConfig';
import { toCrudFieldErrors } from '../utils';
import styles from '../styles.module.css';

type BdmFormValues = {
  name: string;
  contactNumber: string;
  description?: string;
  regionIds: string[];
};

type Props = CrudFormProps<BdmResponse> & { regions: RegionResponse[] };

export const BdmCrudForm = ({ mode, record, regions, onClose, refreshTable }: Props): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const regionOptions = useMemo(() => regions.map(r => ({ label: r.name, value: r.id })), [regions]);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const renderHint = useCallback(
    (): ReactNode =>
      regions.length === 0 ? (
        <Alert type="info" showIcon title={t('lender.regionsBdms.labels.noRegionsHint')} className={styles.noRegionsAlert} />
      ) : null,
    [regions.length, t],
  );

  const renderWarning = useCallback(
    (): ReactNode => (
      <Alert
        type="warning"
        showIcon
        title={t('lender.regionsBdms.labels.noChangeRequestSaveWarning')}
        className={styles.noChangeRequestAlert}
      />
    ),
    [t],
  );

  const onConfirm = useCallback(
    async (values: BdmFormValues): Promise<ValidationError[] | void> => {
      const body = {
        name: values.name,
        description: values.description ?? null,
        contactNumber: values.contactNumber,
        regionIds: values.regionIds ?? [],
      };
      try {
        if (mode === CrudMode.EDIT && record) {
          await bdmUpdate({ path: { id: record.id }, body });
          notification().success({ title: t('lender.regionsBdms.labels.bdmUpdated', { name: values.name }) });
        } else {
          await bdmCreate({ body });
          notification().success({ title: t('lender.regionsBdms.labels.bdmCreated', { name: values.name }) });
        }
        refreshTable();
      } catch (error) {
        const fieldErrors = toCrudFieldErrors(error);
        if (fieldErrors) return transformCrudFormErrors(fieldErrors);
        return Promise.reject(rejectionError(getApiErrorMessage(error)));
      }
    },
    [mode, record, refreshTable, t],
  );

  const crudProps: CrudProps<BdmFormValues> = useMemo(
    () => ({
      ref,
      open,
      schema: getBdmSchema(t, regionOptions, renderHint, renderWarning),
      title: mode === CrudMode.EDIT ? t('lender.regionsBdms.labels.editBdm') : t('lender.regionsBdms.labels.createBdm'),
      labels: { confirm: t('global.buttons.save') },
      initialValues: record
        ? {
            name: record.name,
            contactNumber: record.contactNumber,
            description: record.description ?? undefined,
            regionIds: record.regionIds ?? [],
          }
        : null,
      mode,
      onClose: handleOnClose,
      onConfirm,
    }),
    [open, mode, record, regionOptions, renderHint, renderWarning, t, handleOnClose, onConfirm],
  );

  return <Crud {...crudProps} />;
};
